import { apiClient } from './client'

export type AiMode = 'question' | 'prepare' | 'apply'

export interface AiModel {
  id: string
  name: string
}

export interface AiSession {
  id: number
  project_id: number
  created_by: number
  title: string
  selected_model: string | null
  created_at: string
  updated_at: string
  last_message?: string | null
  last_message_at?: string | null
}

export interface AiAttachment {
  id: number
  message_id: number
  original_filename: string
  mime_type: string | null
  file_size: number
  created_at: string
}

export interface AiMessage {
  id: number
  session_id: number
  role: 'user' | 'assistant' | 'system'
  mode: AiMode | null
  content: string
  model: string | null
  status: 'done' | 'pending' | 'error'
  created_by: number | null
  created_at: string
  attachments?: AiAttachment[]
}

export interface AiJob {
  id: number
  session_id: number
  user_message_id: number
  mode: AiMode
  model: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  started_at: string | null
  completed_at: string | null
  error_text: string | null
  created_at: string
  updated_at: string
}

export async function getAiModels(projectId: number): Promise<{ models: AiModel[]; popular_models: AiModel[] }> {
  const response = await apiClient.get('/api/ai/models', { params: { project_id: projectId } })
  return response.data as { models: AiModel[]; popular_models: AiModel[] }
}

export async function getAiSettings(projectId: number): Promise<{ preferred_mode: AiMode; preferred_model: string | null; allowed_modes?: AiMode[] }> {
  const response = await apiClient.get('/api/ai/settings', { params: { project_id: projectId } })
  return response.data as { preferred_mode: AiMode; preferred_model: string | null; allowed_modes?: AiMode[] }
}

export async function updateAiSettings(payload: {
  project_id: number
  preferred_mode: AiMode
  preferred_model?: string | null
}): Promise<{ preferred_mode: AiMode; preferred_model: string | null }> {
  const response = await apiClient.put('/api/ai/settings', payload)
  return response.data as { preferred_mode: AiMode; preferred_model: string | null }
}

export async function getAiSessions(projectId: number): Promise<AiSession[]> {
  const response = await apiClient.get('/api/ai/sessions', { params: { project_id: projectId } })
  const data = response.data as { sessions: AiSession[] }
  return data.sessions || []
}

export async function createAiSession(payload: {
  project_id: number
  title: string
  selected_model?: string | null
}): Promise<{ id: number }> {
  const response = await apiClient.post('/api/ai/sessions', payload)
  return response.data as { id: number }
}

export async function deleteAiSession(sessionId: number): Promise<{ deleted: boolean }> {
  const response = await apiClient.delete(`/api/ai/sessions/${sessionId}`)
  return response.data as { deleted: boolean }
}

export async function getAiSessionMessages(sessionId: number): Promise<{ messages: AiMessage[]; jobs: AiJob[] }> {
  const response = await apiClient.get(`/api/ai/sessions/${sessionId}/messages`)
  return response.data as { messages: AiMessage[]; jobs: AiJob[] }
}

export async function sendAiMessage(sessionId: number, payload: {
  content: string
  mode: AiMode
  model: string
  files?: File[]
}): Promise<{ user_message_id: number; job_id: number }> {
  if (payload.files && payload.files.length > 0) {
    const formData = new FormData()
    formData.append('content', payload.content)
    formData.append('mode', payload.mode)
    formData.append('model', payload.model)
    payload.files.forEach((file) => {
      formData.append('files[]', file)
    })
    const response = await apiClient.post(`/api/ai/sessions/${sessionId}/messages`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000,
    })
    return response.data as { user_message_id: number; job_id: number }
  } else {
    const response = await apiClient.post(`/api/ai/sessions/${sessionId}/messages`, payload, {
      // HTTP should return quickly because job is processed in background worker.
      timeout: 15000,
    })
    return response.data as { user_message_id: number; job_id: number }
  }
}

export async function uploadAiAttachments(sessionId: number, messageId: number, files: File[]): Promise<AiAttachment[]> {
  const formData = new FormData()
  formData.append('message_id', String(messageId))
  files.forEach((file) => {
    formData.append('files[]', file)
  })

  const response = await apiClient.post(`/api/ai/sessions/${sessionId}/attachments`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data as AiAttachment[]
}
